import { useMemo, useState } from 'react';
import { WheelPicker, WheelPickerWrapper } from '@ncdai/react-wheel-picker';
import styles from './WheelDateTimePicker.module.scss';

const pad2 = (n) => String(n).padStart(2, '0');

const toYmd = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function safeParseTime(value) {
  const s = typeof value === 'string' ? value : '';
  const m = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!m) {
    const now = new Date();
    return { hh: now.getHours(), mm: now.getMinutes() };
  }
  const hh = Math.max(0, Math.min(23, Number(m[1]) || 0));
  const mm = Math.max(0, Math.min(59, Number(m[2]) || 0));
  return { hh, mm };
}

export default function DateTimeWheelPicker({
  date,
  time,
  onDateChange,
  onTimeChange,
  daysBack = 365,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const safeDate = useMemo(() => {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date))
      return date;
    return toYmd(today);
  }, [date, today]);

  const dayOptions = useMemo(() => {
    return Array.from({ length: daysBack }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - i);

      let label = new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
      }).format(d);

      if (i === 0) label = 'Сегодня';
      if (i === 1) label = 'Вчера';

      return { value: toYmd(d), label };
    });
  }, [today, daysBack]);

  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => ({ value: i, label: pad2(i) })),
    []
  );

  const minutes = useMemo(
    () => Array.from({ length: 60 }, (_, i) => ({ value: i, label: pad2(i) })),
    []
  );

  const { hh, mm } = useMemo(() => safeParseTime(time), [time]);

  const setH = (nextH) => onTimeChange?.(`${pad2(nextH)}:${pad2(mm)}`);
  const setM = (nextM) => onTimeChange?.(`${pad2(hh)}:${pad2(nextM)}`);

  const displayLabel = useMemo(() => {
    const opt = dayOptions.find((o) => o.value === safeDate);
    return opt ? opt.label : safeDate;
  }, [dayOptions, safeDate]);

  return (
    <div className={styles.mainContainer}>
      <button
        type="button"
        className={[styles.triggerBtn, isOpen ? styles.triggerBtn_open : ''].join(' ')}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={[styles.triggerDate, isOpen ? styles.triggerDate_open : ''].join(' ')}>{displayLabel}</span>
        <span className={[styles.triggerTime, isOpen ? styles.triggerTime_open : ''].join(' ')}>
          {pad2(hh)}:{pad2(mm)}
        </span>
      </button>

      <div
        className={`${styles.collapseArea} ${
          isOpen ? styles.collapseArea_open : ''
        }`}
      >
        <WheelPickerWrapper className={styles.wrapper}>
          <WheelPicker
            options={dayOptions}
            value={safeDate}
            onValueChange={(v) => onDateChange?.(v)}
            visibleCount={10}
          />
          <div className={styles.picker__time}>
            <WheelPicker
              options={hours}
              value={hh}
              onValueChange={setH}
              infinite
              visibleCount={10}
            />
            <span className={styles.picker__dots}>:</span>
            <WheelPicker
              options={minutes}
              value={mm}
              onValueChange={setM}
              infinite
              visibleCount={10}
            />
          </div>
        </WheelPickerWrapper>
      </div>
    </div>
  );
}